<template>
  <div>
    <div class="categories-chart">
      <div>
        <strong>Primary Category</strong>
        <Legend :chart="primaryChart"
          :categories="primaryCategories"
          :current-categories="currentCategories.primary.name"
          :hovered-category="hoveredCategory"
          :hovered-chart-id="hoveredChartId" />
        <div class="d-flex justify-content-center align-items-center gap-2 mt-auto">
          <div v-if="!Number.isNaN(primaryTotalIncluded)" class="text-right font-size-14 font-weight-semi-bold">
            NOT included
            <br>
            <span class="font-weight-bolder">{{ 100 - primaryTotalIncluded }}%</span>
          </div>
          <div id="primary-category-chart" class="chart" />
          <div v-if="!Number.isNaN(primaryTotalIncluded)" class="font-size-14 color-green font-weight-semi-bold">
            Included to your list
            <br>
            <span class="font-weight-bolder">{{ primaryTotalIncluded }}%</span>
          </div>
        </div>
      </div>
      <div>
        <strong>Secondary Category</strong>
        <Legend :chart="secondaryChart"
          :categories="secondaryCategories"
          :current-categories="currentCategories.additional"
          :hovered-category="hoveredCategory"
          :hovered-chart-id="hoveredChartId" />
        <div class="d-flex justify-content-center align-items-center gap-4 mt-auto">
          <div v-if="!Number.isNaN(secondaryTotalIncluded)" class="text-right font-size-14 font-weight-semi-bold">
            NOT included
            <br>
            <span class="font-weight-bolder">{{ 100 - secondaryTotalIncluded }}%</span>
          </div>
          <div id="secondary-category-chart" class="chart" />
          <div v-if="!Number.isNaN(secondaryTotalIncluded)" class="font-size-14 color-green font-weight-semi-bold">
            Included to your list
            <br>
            <span class="font-weight-bolder">{{ secondaryTotalIncluded }}%</span>
          </div>
        </div>
      </div>
    </div>

    <h2 class="h4 mb-2 text-dark">Competitors</h2>

    <VueSlimTable
      ref="table"
      :source="getCompetitors"
      :columns="columns"
      class="table table-borderless table-competitors">
      <template #cell:name="{ row }">
        {{ row.name }}
        <small class="d-block">{{ row.formattedAddress }}</small>
      </template>
      <template #cell:primary="{ value }">
        {{ value.name }}
      </template>
      <template #cell:additional="{ value }">
        <div class="d-flex flex-wrap gap-2">
          <div v-for="{ name, remoteId } in value" :key="remoteId" class="tag">
            <div class="tag-text">
              <span>{{ name }}</span>
            </div>
          </div>
        </div>
      </template>
      <template #pagination>
        <span>&nbsp;</span>
      </template>
    </VueSlimTable>
  </div>
</template>

<script>
import Highcharts from 'highcharts'
import Legend from './categories/legend'

const pieChartOptions = {
  chart: {
    type: 'pie',
    spacing: [0, 0, 0, 0]
  },
  credits: { enabled: false },
  title: { text: '' },
  tooltip: { enabled: false },
  series: [{
    borderWidth: 3,
    dataLabels: {
      enabled: true,
      formatter() {
        return `<span style="color: ${this.point.color}">${Math.round(this.point.percentage)}%</span>`
      },
      connectorColor: 'transparant',
      distance: -75,
      className: 'data-label-text',
      useHTML: true,
      y: -5
    },
    ignoreHiddenPoint: false,
    innerSize: '65%',
    states: {
      hover: {
        brightness: 0,
        enabled: true,
        halo: { opacity: 1, size: -50 }
      },
      inactive: { enabled: false }
    }
  }]
}

const onChartCompete = (chart) => {
  chart.series[0].points.forEach((point) => point.dataLabels[0].hide())
}

export default {
  components: { Legend },
  props: {
    report: { type: Object, required: true }
  },
  data() {
    return {
      primaryCategories: [],
      secondaryCategories: [],
      primaryChart: null,
      secondaryChart: null,
      hoveredCategory: '',
      hoveredChartId: '',
      primaryIncluded: 0,
      secondaryIncluded: 0
    }
  },
  created() {
    this.columns = [
      { key: 'name', title: 'Name', orderable: false },
      { key: 'primary', title: 'Primary Category', orderable: false },
      { key: 'additional', title: 'Secondary Category', orderable: false }
    ]
  },
  computed: {
    currentCategories() {
      return this.report.data.sections.categories.current
    },
    primaryTotalIncluded() {
      return Math.round(this.primaryIncluded / this.getCompetitors().length * 100)
    },
    secondaryTotalIncluded() {
      const total = this.getCompetitors().reduce((acc, { additional }) => acc + additional.length, 0)
      return Math.round(this.secondaryIncluded / total * 100)
    }
  },
  mounted() {
    const primaryCategories = new Set()
    const secondaryCategories = new Set()
    const dataPrimaryCategories = []
    const dataSecondaryCategories = []

    this.getCompetitors().forEach(({ primary, additional }) => {
      primaryCategories.add(primary.name)
      if (primary.name === this.currentCategories.primary.name) this.primaryIncluded++

      const foundPrimaryCategoryIndex = dataPrimaryCategories.findIndex(({ name }) => name === primary.name)
      if (foundPrimaryCategoryIndex === -1) {
        dataPrimaryCategories.push({
          name: primary.name,
          y: 1,
          color: primary.name === this.currentCategories.primary.name ? '#29ba6c' : '#d5dae2'
        })
      } else {
        dataPrimaryCategories[foundPrimaryCategoryIndex].y++
      }

      additional.forEach((secondary) => {
        secondaryCategories.add(secondary.name)
        if (this.matchCategories(this.currentCategories.additional, secondary.name)) this.secondaryIncluded++

        const foundSecondaryCategoryIndex = dataSecondaryCategories.findIndex(({ name }) => name === secondary.name)
        if (foundSecondaryCategoryIndex === -1) {
          dataSecondaryCategories.push({
            name: secondary.name,
            y: 1,
            color: this.matchCategories(this.currentCategories.additional, secondary.name) ? '#29ba6c' : '#d5dae2'
          })
        } else {
          dataSecondaryCategories[foundSecondaryCategoryIndex].y++
        }
      })
    })

    this.primaryCategories = Array.from(primaryCategories)
    this.secondaryCategories = Array.from(secondaryCategories)

    const vm = this
    pieChartOptions.series[0].point = {
      events: {
        mouseOver() {
          this.dataLabels[0].show()
          vm.hoveredCategory = this.name
          vm.hoveredChartId = this.series.chart.options.id
        },
        mouseOut() {
          this.dataLabels[0].hide()
          vm.hoveredCategory = ''
          vm.hoveredChartId = ''
        }
      }
    }

    const primaryPieChartOptions = { ...pieChartOptions, id: 'primary' }
    primaryPieChartOptions.series[0].data = dataPrimaryCategories
    this.primaryChart = Highcharts.chart('primary-category-chart', primaryPieChartOptions, onChartCompete)

    const secondaryPieChartOptions = { ...pieChartOptions, id: 'secondary' }
    secondaryPieChartOptions.series[0].data = dataSecondaryCategories
    this.secondaryChart = Highcharts.chart('secondary-category-chart', secondaryPieChartOptions, onChartCompete)
  },
  methods: {
    getCompetitors() {
      return this.report.data.sections.categories.competitors
    },
    matchCategories(currentCategories, competitorCategory) {
      return Boolean(currentCategories.find(({ name }) => name === competitorCategory))
    }
  }
}
</script>
